import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@taunggyi-plus/auth";

interface ChatThread {
  id: string;
  customer_name: string;
  unread: number;
  preview: string | null;
}
interface ChatMessage {
  id: number;
  body: string;
  from_admin: boolean;
  media_type: string | null;
  created_at: string;
}
const MAX_FILE = 5 * 1024 * 1024;
const mediaTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "audio/webm",
  "audio/ogg",
  "audio/mp4",
  "audio/x-m4a",
  "audio/wav",
  "audio/x-wav",
];

function ChatIcon({ name }: { name: "chat" | "send" | "photo" | "mic" }) {
  const paths = {
    chat: (
      <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" />
    ),
    send: (
      <>
        <path d="m22 2-7 20-4-9-9-4 20-7ZM22 2 11 13" />
      </>
    ),
    photo: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="8" cy="8" r="1.5" />
        <path d="m21 15-5-5L5 21" />
      </>
    ),
    mic: (
      <>
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function MessageText({ text }: { text: string }) {
  const parts: ReactNode[] = text.split(/(https?:\/\/[^\s<>]+)/g).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a href={part} key={index} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  );
  return <p className="support-chat-text">{parts}</p>;
}

function PrivateMedia({ message }: { message: ChatMessage }) {
  const { client } = useAuth();
  const [url, setUrl] = useState("");
  const [error, setError] = useFeedback("", "error");
  const [busy, setBusy] = useState(false);
  const active = useRef(true);
  const currentUrl = useRef("");
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      URL.revokeObjectURL(currentUrl.current);
    };
  }, []);
  async function load() {
    if (!client) return;
    setBusy(true);
    setError("");
    try {
      const result = await client.rpc("support_attachment", { message_id: message.id });
      if (result.error) throw new Error(result.error.message);
      if (!active.current) return;
      const data = result.data as { type: string; base64: string };
      const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
      const next = URL.createObjectURL(new Blob([bytes], { type: data.type }));
      currentUrl.current = next;
      setUrl(next);
    } catch (e) {
      if (active.current) setError(e instanceof Error ? e.message : "Could not load attachment");
    } finally {
      if (active.current) setBusy(false);
    }
  }
  return (
    <div className="support-chat-media">
      {url ? (
        message.media_type?.startsWith("image/") ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="Chat photo" />
          </a>
        ) : (
          <audio controls src={url} preload="metadata" />
        )
      ) : (
        <button
          type="button"
          className="button button--secondary"
          onClick={() => void load()}
          disabled={busy}
        >
          {busy
            ? "Loading…"
            : message.media_type?.startsWith("image/")
              ? "View photo"
              : "Play voice message"}
        </button>
      )}
      {error && (
        <small role="alert">
          {error}{" "}
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </small>
      )}
    </div>
  );
}

function Conversation({
  threadId,
  admin,
  onCreated,
  onRead,
}: {
  threadId: string | null;
  admin: boolean;
  onCreated: (id: string) => void;
  onRead: () => void;
}) {
  const { client } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState<Blob | null>(null);
  const [preview, setPreview] = useState("");
  const previewUrl = useRef("");
  const [error, setError] = useFeedback("", "error");
  const [sendError, setSendError] = useFeedback("", "error");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(Boolean(threadId));
  const [older, setOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const requestingMic = useRef(false);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const active = useRef(true);
  const requestId = useRef(crypto.randomUUID());
  const lastRead = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const followBottom = useRef(true);
  const firstLoad = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      if (recorder.current?.state === "recording") recorder.current.stop();
      stream.current?.getTracks().forEach((track) => track.stop());
      if (timer.current) clearInterval(timer.current);
      URL.revokeObjectURL(previewUrl.current);
    };
  }, []);
  useEffect(() => {
    if (followBottom.current) bottom.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const load = useCallback(async () => {
    if (!client || !threadId) return;
    const result = await client.rpc("support_messages_list", { target_thread: threadId });
    if (!active.current) return;
    setLoading(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setError("");
    const next = result.data as ChatMessage[];
    if (firstLoad.current) {
      setOlder(next.length === 50);
      firstLoad.current = false;
    }
    setMessages((current) => {
      const merged = new Map(current.map((m) => [m.id, m]));
      next.forEach((m) => merged.set(m.id, m));
      return [...merged.values()].sort((a, b) => a.id - b.id);
    });
    const latest = next.at(-1)?.id ?? 0;
    if (latest > lastRead.current && document.visibilityState === "visible") {
      const read = await client.rpc("support_mark_read", {
        target_thread: threadId,
        through_id: latest,
      });
      if (!read.error && active.current) {
        lastRead.current = latest;
        onRead();
      }
    }
  }, [client, threadId, setError, onRead]);
  useEffect(() => {
    let cancelled = false;
    let running = false;
    let timeout: ReturnType<typeof setTimeout>;
    async function poll() {
      if (running || cancelled) return;
      running = true;
      if (document.visibilityState === "visible") await load();
      running = false;
      if (!cancelled) timeout = setTimeout(() => void poll(), 5000);
    }
    const wake = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timeout);
        void poll();
      }
    };
    document.addEventListener("visibilitychange", wake);
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [load]);

  async function loadOlder() {
    if (!client || !threadId || !messages.length) return;
    setLoadingOlder(true);
    const result = await client.rpc("support_messages_list", {
      target_thread: threadId,
      before_id: messages[0]?.id,
    });
    if (!active.current) return;
    setLoadingOlder(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    const next = result.data as ChatMessage[];
    setOlder(next.length === 50);
    followBottom.current = false;
    setMessages((current) => [...next, ...current]);
  }
  function chooseFile(blob: Blob | null) {
    setSendError("");
    if (
      blob &&
      (!mediaTypes.includes(blob.type.split(";")[0] ?? "") ||
        blob.size > MAX_FILE ||
        blob.size === 0)
    ) {
      setSendError(
        "Choose a photo or voice file up to 5 MB (JPEG, PNG, WebP, GIF, WebM, Ogg, M4A, or WAV).",
      );
      return;
    }
    requestId.current = crypto.randomUUID();
    URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = blob ? URL.createObjectURL(blob) : "";
    setPreview(previewUrl.current);
    setFile(blob);
  }
  async function startRecording() {
    if (requestingMic.current || recorder.current?.state === "recording") return;
    setSendError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setSendError(
        "Voice recording is unavailable in this browser. Use Attach voice file instead.",
      );
      return;
    }
    try {
      requestingMic.current = true;
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!active.current) {
        microphone.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = microphone;
      const type = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"].find((t) =>
        MediaRecorder.isTypeSupported(t),
      );
      if (!type) {
        microphone.getTracks().forEach((t) => t.stop());
        throw new Error(
          "This browser cannot record a supported voice format. Attach a voice file instead.",
        );
      }
      const next = new MediaRecorder(microphone, { mimeType: type });
      recorder.current = next;
      const chunks: Blob[] = [];
      let size = 0;
      next.ondataavailable = (event) => {
        chunks.push(event.data);
        size += event.data.size;
        if (size > MAX_FILE && next.state === "recording") next.stop();
      };
      next.onstop = () => {
        microphone.getTracks().forEach((t) => t.stop());
        if (timer.current) clearInterval(timer.current);
        if (active.current) {
          setRecording(false);
          chooseFile(new Blob(chunks, { type }));
        }
      };
      next.onerror = () => {
        microphone.getTracks().forEach((t) => t.stop());
        if (timer.current) clearInterval(timer.current);
        if (active.current) {
          setRecording(false);
          setSendError("Recording failed. Please try again.");
        }
      };
      setSeconds(0);
      setRecording(true);
      next.start(1000);
      let elapsed = 0;
      timer.current = setInterval(() => {
        elapsed += 1;
        setSeconds(elapsed);
        if (elapsed >= 60 && next.state === "recording") next.stop();
      }, 1000);
    } catch (e) {
      stream.current?.getTracks().forEach((t) => t.stop());
      if (active.current)
        setSendError(
          e instanceof Error && e.name === "NotAllowedError"
            ? "Microphone access was denied. Allow it in your browser settings, or attach a voice file."
            : e instanceof Error
              ? e.message
              : "Could not access microphone",
        );
    } finally {
      requestingMic.current = false;
    }
  }
  async function send() {
    if (!client || sending || recording || (!text.trim() && !file)) return;
    setSending(true);
    setSendError("");
    try {
      let base64: string | null = null;
      if (file) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = "";
        for (let i = 0; i < bytes.length; i += 32768)
          binary += String.fromCharCode(...bytes.subarray(i, i + 32768));
        base64 = btoa(binary);
      }
      const result = await client.rpc("support_send", {
        request_id: requestId.current,
        target_thread: threadId,
        message_body: text.trim(),
        attachment_type: file?.type ?? null,
        attachment_base64: base64,
      });
      if (result.error) throw new Error(result.error.message);
      if (!active.current) return;
      setText("");
      chooseFile(null);
      requestId.current = crypto.randomUUID();
      followBottom.current = true;
      if (!threadId) onCreated((result.data as { thread_id: string }).thread_id);
      else {
        await load();
        onRead();
      }
    } catch (e) {
      if (active.current)
        setSendError(e instanceof Error ? e.message : "Message not sent. Please retry.");
    } finally {
      if (active.current) setSending(false);
    }
  }

  return (
    <>
      <div
        className="support-chat-messages"
        ref={list}
        onScroll={() => {
          const el = list.current;
          if (el) followBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        }}
        aria-label="Conversation"
      >
        {older && (
          <button
            className="button button--secondary"
            type="button"
            disabled={loadingOlder}
            onClick={() => void loadOlder()}
          >
            {loadingOlder ? "Loading…" : "Load earlier messages"}
          </button>
        )}
        {loading && <p role="status">Loading conversation…</p>}
        {error && (
          <p role="alert">
            {error}{" "}
            <button type="button" onClick={() => void load()}>
              Retry
            </button>
          </p>
        )}
        {!loading && !error && !messages.length && (
          <div className="support-chat-empty">
            <ChatIcon name="chat" />
            <h3>How can we help?</h3>

            <small>
              For phone support: <a href="tel:+959798392286">+959798392286</a>
            </small>
          </div>
        )}
        {messages.map((message) => (
          <article
            key={message.id}
            className={`support-chat-message ${message.from_admin === admin ? "is-mine" : ""}`}
          >
            <small>{message.from_admin ? "Link Support" : admin ? "Customer" : "You"}</small>
            {message.body && <MessageText text={message.body} />}
            {message.media_type && <PrivateMedia message={message} />}
            <time dateTime={message.created_at}>
              {new Date(message.created_at).toLocaleString([], {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </time>
          </article>
        ))}
        <div ref={bottom} />
      </div>
      <form
        className="support-chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        {sendError && (
          <p role="alert" className="form-message">
            {sendError}
          </p>
        )}
        {file && (
          <div className="support-chat-preview">
            {file.type.startsWith("image/") ? (
              <img src={preview} alt="Photo ready to send" />
            ) : (
              <audio src={preview} controls />
            )}
            <button
              type="button"
              disabled={sending || recording}
              onClick={() => {
                chooseFile(null);
                setPreview("");
              }}
            >
              Remove attachment
            </button>
          </div>
        )}
        <textarea
          aria-label="Message"
          placeholder="Write a message or paste a link…"
          value={text}
          maxLength={4000}
          rows={2}
          disabled={sending}
          onChange={(event) => {
            setText(event.target.value);
            requestId.current = crypto.randomUUID();
          }}
        />
        <div className="support-chat-tools">
          <label className="support-chat-icon-button" title="Attach photo">
            <ChatIcon name="photo" />
            <input
              className="skip-link"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              aria-label="Attach photo"
              disabled={sending || recording}
              onChange={(event) => {
                chooseFile(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />
          </label>
          <button
            type="button"
            className={`support-chat-icon-button ${recording ? "is-recording" : ""}`}
            aria-label={recording ? "Stop recording" : "Record voice message"}
            title={recording ? "Stop recording" : "Record voice message"}
            disabled={sending}
            onClick={() => (recording ? recorder.current?.stop() : void startRecording())}
          >
            {recording ? <span>■</span> : <ChatIcon name="mic" />}
          </button>
          <label className="support-chat-file-label">
            Attach voice file
            <input
              className="skip-link"
              type="file"
              accept="audio/webm,audio/ogg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,.m4a"
              aria-label="Attach voice file"
              disabled={sending || recording}
              onChange={(event) => {
                chooseFile(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />
          </label>
          <button
            className="support-chat-icon-button support-chat-send"
            type="submit"
            aria-label="Send message"
            title="Send message"
            disabled={sending || recording || (!text.trim() && !file)}
          >
            {sending ? "…" : <ChatIcon name="send" />}
          </button>
        </div>
        <small role="status">
          {recording
            ? `Recording ${seconds}s / 60s — stop to preview before sending`
            : "Photos and voice: up to 5 MB · Voice recording: 60 seconds"}
        </small>
      </form>
    </>
  );
}

function SupportChatSession({ admin = false }: { admin?: boolean }) {
  const { client, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useFeedback("", "error");
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!admin) return;
    const openInbox = () => {
      setSelected(null);
      setOpen(true);
    };
    window.addEventListener("taunggyi:open-support", openInbox);
    return () => window.removeEventListener("taunggyi:open-support", openInbox);
  }, [admin]);
  const refresh = useCallback(async () => {
    if (!client || !user) return;
    const result = await client.rpc("support_threads_list");
    setLoading(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setError("");
    setThreads(result.data as ChatThread[]);
    setHasLoaded(true);
  }, [client, setError, user]);
  useEffect(() => {
    let cancelled = false;
    let running = false;
    let timeout: ReturnType<typeof setTimeout>;
    async function poll() {
      if (running || cancelled) return;
      running = true;
      if (document.visibilityState === "visible") await refresh();
      running = false;
      if (!cancelled) timeout = setTimeout(() => void poll(), open ? 7000 : 20000);
    }
    const wake = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timeout);
        void poll();
      }
    };
    document.addEventListener("visibilitychange", wake);
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [refresh, open]);
  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
  }, [open]);
  const onRead = useCallback(() => {
    void refresh();
  }, [refresh]);
  const unread = threads.reduce((sum, thread) => sum + Number(thread.unread), 0);
  const threadId = admin ? selected : (threads[0]?.id ?? selected);
  return (
    <>
      <button
        className="support-chat-launcher"
        type="button"
        aria-label={
          admin
            ? `Support inbox${unread ? `, ${unread} unread messages` : ""}`
            : `Chat with support${unread ? `, ${unread} unread messages` : ""}`
        }
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <ChatIcon name="chat" />
        {unread > 0 && <span className="support-chat-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      <dialog
        ref={dialog}
        className="support-chat-dialog"
        aria-labelledby="support-chat-title"
        onCancel={() => setOpen(false)}
        onClose={() => setOpen(false)}
      >
        <header className="support-chat-header">
          {admin && selected && (
            <button
              type="button"
              className="support-chat-icon-button"
              aria-label="Back to inbox"
              onClick={() => setSelected(null)}
            >
              ←
            </button>
          )}
          <div>
            <h2 id="support-chat-title">
              {admin
                ? selected
                  ? (threads.find((t) => t.id === selected)?.customer_name ?? "Customer")
                  : "Support inbox"
                : "Link Support"}
            </h2>
          </div>
          <button
            type="button"
            className="support-chat-icon-button"
            aria-label="Close chat"
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        </header>
        {open &&
          (!user ? (
            <div className="support-chat-empty">
              <p>Sign in to chat with customer support.</p>
              <a className="button" href="/more/profile">
                Sign in
              </a>
              <a href="tel:+959798392286">Call +959798392286</a>
            </div>
          ) : admin && !selected ? (
            <div className="support-chat-inbox">
              {loading && <p>Loading inbox…</p>}
              {error && (
                <p role="alert">
                  {error}{" "}
                  <button type="button" onClick={() => void refresh()}>
                    Retry
                  </button>
                </p>
              )}
              {!loading && !error && !threads.length && <p>No customer conversations yet.</p>}
              {threads.map((thread) => (
                <button
                  type="button"
                  className="support-chat-thread"
                  key={thread.id}
                  onClick={() => setSelected(thread.id)}
                >
                  <span>
                    <strong>{thread.customer_name}</strong>
                    <small>{thread.preview ?? "New conversation"}</small>
                  </span>
                  {Number(thread.unread) > 0 && <b>{thread.unread}</b>}
                </button>
              ))}
            </div>
          ) : loading ? (
            <div className="support-chat-empty" role="status">
              Loading conversation…
            </div>
          ) : error && !hasLoaded ? (
            <div className="support-chat-empty" role="alert">
              {error}
              <button type="button" className="button" onClick={() => void refresh()}>
                Retry
              </button>
            </div>
          ) : (
            <Conversation
              key={`${user.id}:${threadId ?? "new"}`}
              threadId={threadId}
              admin={admin}
              onCreated={(id) => {
                setSelected(id);
                void refresh();
              }}
              onRead={onRead}
            />
          ))}
      </dialog>
    </>
  );
}

export function SupportChat({ admin = false }: { admin?: boolean }) {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return <SupportChatSession key={user.id} admin={admin} />;
}
